import { Badge } from "@/components/atoms";

/**
 * Client-facing status grouping. Backend job statuses (JobStatus enum) are internal codes (ADA/ADC/…)
 * that clients don't recognise, so we collapse them into four friendly buckets. Single source of
 * truth for both the Booking History status column and BookingAction's action gating.
 *
 * Grouping (confirmed with product — CRV sits under In Progress, not Completed):
 *   Upcoming    : awaiting_driver_allocation (ADA), driver_allocated_not_confirmed (ADC),
 *                 driver_allocated_confirmed (ALC)
 *   In Progress : on_way_to_job (DOW), commenced (COM), complete_awaiting_review (CRV)
 *   Completed   : closed (CSD)
 *   Cancelled   : cancelled (CND)
 */
export type BookingStatusGroup = "upcoming" | "in_progress" | "completed" | "cancelled";

/**
 * The real `job_status` values. These are the only ones a QUERY may use — the operator job endpoint
 * validates `status[]` against exactly this set and rejects the whole request on one bad value.
 */
const CANONICAL_STATUS_TO_GROUP: Record<string, BookingStatusGroup> = {
  // Upcoming
  awaiting_driver_allocation: "upcoming",
  driver_allocated_not_confirmed: "upcoming",
  driver_allocated_confirmed: "upcoming",
  // In Progress
  on_way_to_job: "in_progress",
  commenced: "in_progress",
  complete_awaiting_review: "in_progress",
  // Completed
  closed: "completed",
  // Cancelled
  cancelled: "cancelled",
};

/**
 * Short forms seen in older payloads. Badging only — never send these to an endpoint.
 *
 * Kept apart from the canonical set rather than mixed in with a comment, because mixing them is
 * exactly how they ended up in a `status[]` query and 400'd it: "Job Status: Is not valid".
 */
const ALIAS_STATUS_TO_GROUP: Record<string, BookingStatusGroup> = {
  driver_allocated: "upcoming",
  confirmed: "upcoming",
  on_way: "in_progress",
  picked_up: "in_progress",
};

const STATUS_TO_GROUP: Record<string, BookingStatusGroup> = { ...CANONICAL_STATUS_TO_GROUP, ...ALIAS_STATUS_TO_GROUP };

export const getBookingStatusGroup = (status: string): BookingStatusGroup | null => STATUS_TO_GROUP[status] ?? null;

/**
 * The job statuses in a group, for endpoints that filter by status rather than by stage (the operator
 * job list takes `status[]`). Canonical values only — see ALIAS_STATUS_TO_GROUP.
 *
 * Inverted from the map rather than written out again, so a status cannot be badged as one stage and
 * filtered as another.
 */
export const getStatusesForGroup = (group: BookingStatusGroup): string[] =>
  Object.entries(CANONICAL_STATUS_TO_GROUP)
    .filter(([, g]) => g === group)
    .map(([status]) => status);

/** The four stages in the order a booking moves through them — used for the filter options. */
export const BOOKING_STATUS_GROUPS: BookingStatusGroup[] = ["upcoming", "in_progress", "completed", "cancelled"];

export const GROUP_DISPLAY: Record<BookingStatusGroup, { label: string; type: "info" | "warning" | "success" | "danger" }> = {
  upcoming: { label: "Upcoming", type: "info" },
  in_progress: { label: "In Progress", type: "warning" },
  completed: { label: "Completed", type: "success" },
  cancelled: { label: "Cancelled", type: "danger" },
};

/** A grouped status badge for the Booking History table. Unknown statuses render a neutral dash. */
export const BookingStatusBadge = ({ status }: { status: string }) => {
  const group = getBookingStatusGroup(status);
  if (!group) return <span className="text-neutral-dark-gray">-</span>;
  const { label, type } = GROUP_DISPLAY[group];
  return <Badge type={type} message={label} size="sm" />;
};
